/**
 * Motor de puntuacion de Time Cap.
 *
 * Espejo exacto de supabase/migrations/0002_scoring.sql. Existe en dos
 * lugares a proposito: el servidor es la autoridad, pero la tablet del
 * juez tiene que poder rankear sin conexion para mostrar posiciones
 * provisionales en medio del heat.
 *
 * Regla unica: toda puntuacion se normaliza a "menor es mejor".
 */
import { StatusTier, } from './types.js';
import { formatScore } from './format.js';
/**
 * Deriva las claves de ordenamiento de un score crudo.
 * Equivalente a la funcion compute_score_sort() de Postgres.
 */
export function computeSortKey(workout, score) {
    // No-resultados: al final de la tabla, siempre.
    if (score.status === 'dnf' ||
        score.status === 'dns' ||
        score.status === 'dq' ||
        score.status === 'withdrawn' ||
        score.status === 'pending') {
        const statusTier = score.status === 'pending'
            ? StatusTier.PENDING
            : score.status === 'dnf'
                ? StatusTier.DNF
                : StatusTier.DNS;
        return { statusTier, primarySort: Infinity, tiebreakSort: null };
    }
    const tiebreakSort = score.tiebreakSeconds ?? null;
    switch (workout.scoreType) {
        case 'time': {
            if (score.capped) {
                // Detras de todos los que terminaron; entre ellos, por reps
                // restantes: menos restantes, mejor.
                const remaining = Math.max((workout.repTarget ?? 0) - (score.reps ?? 0), 0);
                return {
                    statusTier: StatusTier.CAPPED,
                    primarySort: (workout.timeCapSeconds ?? 0) + remaining,
                    tiebreakSort,
                };
            }
            if (score.timeSeconds == null) {
                throw new Error('Un score "for time" necesita timeSeconds o capped = true');
            }
            return { statusTier: StatusTier.FINISHED, primarySort: score.timeSeconds, tiebreakSort };
        }
        case 'reps':
            return { statusTier: StatusTier.FINISHED, primarySort: -(score.reps ?? 0), tiebreakSort };
        case 'load':
            return { statusTier: StatusTier.FINISHED, primarySort: -(score.loadKg ?? 0), tiebreakSort };
        case 'distance':
            return {
                statusTier: StatusTier.FINISHED,
                primarySort: -(score.distanceM ?? 0),
                tiebreakSort,
            };
    }
}
/** Puntos de una posicion. Equivalente a points_for_place(). */
export function pointsForPlace(table, place) {
    // Sin tabla configurada => sistema "golf": la posicion es el puntaje.
    if (!table || table.kind === 'golf')
        return place;
    const points = table.rows[place];
    return points ?? table.defaultPoints ?? 0;
}
function compareSortKeys(a, b) {
    if (a.statusTier !== b.statusTier)
        return a.statusTier - b.statusTier;
    // Ojo: Infinity !== Infinity es false, asi que dos DNS quedan empatados
    // en vez de producir NaN al restarlos.
    if (a.primarySort !== b.primarySort)
        return a.primarySort - b.primarySort;
    if (a.tiebreakSort === null && b.tiebreakSort === null)
        return 0;
    if (a.tiebreakSort === null)
        return 1; // nulls last
    if (b.tiebreakSort === null)
        return -1;
    return a.tiebreakSort - b.tiebreakSort;
}
/**
 * Ranking estilo SQL RANK(): los empatados comparten la posicion mas baja
 * y la siguiente posicion salta. 1, 1, 3 -- nunca 1, 1, 2.
 */
function assignRanks(items, compare) {
    const sorted = [...items].sort(compare);
    const out = [];
    let i = 0;
    while (i < sorted.length) {
        let j = i;
        while (j + 1 < sorted.length && compare(sorted[j], sorted[j + 1]) === 0)
            j++;
        const place = i + 1;
        const tieCount = j - i + 1;
        for (let k = i; k <= j; k++)
            out.push({ item: sorted[k], place, tieCount });
        i = j + 1;
    }
    return out;
}
/**
 * Puntos de una posicion con empate, repartiendo el promedio de las
 * posiciones ocupadas. Dos primeros lugares en la tabla de 100 se llevan
 * (100 + 95) / 2 = 97.5 cada uno, y el siguiente atleta es 3er lugar.
 * Es la regla del CrossFit Games.
 */
export function pointsForTiedPlace(table, place, tieCount) {
    let sum = 0;
    for (let p = place; p < place + tieCount; p++)
        sum += pointsForPlace(table, p);
    return sum / tieCount;
}
/**
 * Rankea un WOD dentro de una division.
 *
 * Se parte de las inscripciones, no de los scores: un atleta sin score
 * capturado aparece igual, empatado en el ultimo lugar como DNS. Es lo
 * que hace una competencia real -- no marcar no es estar ausente de la
 * tabla, es haber sacado el peor resultado posible.
 */
export function rankWorkout({ workout, registrationIds, scores, scoringTable, }) {
    const entries = registrationIds.map((registrationId) => {
        const raw = scores.get(registrationId);
        const score = raw ?? {
            workoutId: workout.id,
            registrationId,
            status: 'dns',
        };
        const key = computeSortKey(workout, score);
        return { registrationId, score, key };
    });
    return assignRanks(entries, (a, b) => compareSortKeys(a.key, b.key)).map(({ item, place, tieCount }) => {
        const points = pointsForTiedPlace(scoringTable, place, tieCount);
        return {
            registrationId: item.registrationId,
            workoutId: workout.id,
            status: item.score.status,
            statusTier: item.key.statusTier,
            primarySort: item.key.primarySort,
            tiebreakSort: item.key.tiebreakSort,
            place,
            tieCount,
            points,
            weightedPoints: points * (workout.weight ?? 1),
            displayValue: formatScore(workout, item.score),
        };
    });
}
/**
 * Clasificacion general de una division.
 *
 * Desempates, en orden: puntos totales -> mas victorias de WOD ->
 * mejor posicion individual. El head-to-head del ultimo WOD se resuelve
 * a mano desde el panel de admin: automatizarlo produce resultados
 * injustos cuando los atletas no compitieron en el mismo heat.
 */
export function computeStandings({ workouts, registrationIds, scores, scoringTable, }) {
    const active = workouts.filter((w) => w.status !== 'draft');
    const byWorkout = new Map();
    for (const s of scores) {
        let m = byWorkout.get(s.workoutId);
        if (!m)
            byWorkout.set(s.workoutId, (m = new Map()));
        m.set(s.registrationId, s);
    }
    const resultsByRegistration = new Map();
    for (const id of registrationIds)
        resultsByRegistration.set(id, []);
    for (const workout of active) {
        const results = rankWorkout({
            workout,
            registrationIds,
            scores: byWorkout.get(workout.id) ?? new Map(),
            scoringTable,
        });
        for (const r of results)
            resultsByRegistration.get(r.registrationId).push(r);
    }
    const golf = !scoringTable || scoringTable.kind === 'golf';
    const aggregates = registrationIds.map((registrationId) => {
        const results = (resultsByRegistration.get(registrationId) ?? []).sort((a, b) => (active.find((w) => w.id === a.workoutId)?.sortOrder ?? 0) -
            (active.find((w) => w.id === b.workoutId)?.sortOrder ?? 0));
        return {
            registrationId,
            results,
            totalPoints: results.reduce((sum, r) => sum + r.weightedPoints, 0),
            wins: results.filter((r) => r.place === 1).length,
            bestPlace: results.length ? Math.min(...results.map((r) => r.place)) : Number.MAX_SAFE_INTEGER,
            scoredCount: results.filter((r) => r.statusTier <= StatusTier.CAPPED).length,
        };
    });
    return assignRanks(aggregates, (a, b) => {
        const at = golf ? a.totalPoints : -a.totalPoints;
        const bt = golf ? b.totalPoints : -b.totalPoints;
        if (at !== bt)
            return at - bt;
        if (a.wins !== b.wins)
            return b.wins - a.wins; // mas victorias primero
        return a.bestPlace - b.bestPlace;
    }).map(({ item, place }) => ({ ...item, place }));
}
//# sourceMappingURL=scoring.js.map